import styled from "@emotion/styled"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import { PSpinner } from "@porsche-design-system/components-react"
import type { RequestType } from "@slfinrtl/cognito-react"
import { useCognito } from "@slfinrtl/cognito-react"
import { isMarketManager } from "@slfinrtl/ppn-roles"
import type { PropsWithChildren } from "react"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { getJwtPayload } from "src/common/jwt/jwtUtils"
import { mapLegacyEnvironment } from "src/common/mapLegacyEnvironment/mapLegacyEnvironment"
import { AppTitle, CognitoErrorBanner, MainNavigation } from "src/components"
import { AdobeAnalytics } from "src/components/adobeAnalytics"
import { ErrorBannerProvider } from "src/context/errorBanner/ErrorBannerProvider"
import { ModalProvider } from "src/context/modal/ModalProvider"
import { TranslationsContext } from "src/context/translations/TranslationsContext"
import { TranslationsProvider } from "src/context/translations/TranslationsProvider"
import { getCognitoConfig } from "../../common/getCognitoConfig"
import { newsListMessages } from "../newsList/components/newsList.messages"
import { useHasNewsAccess } from "../newsList/hooks/useHasNewsAccess/useHasNewsAccess"
import { DealershipListRoutes } from "./DealershipListRoutes"
import { getDealershipListNavItems } from "./common/getDealershipListNavItems/getDealershipListNavItems"
import { useDealershipRedirect } from "./hooks/useDealershipRedirect/useDealershipRedirect"
import type { RequestContextProps } from "./requestContext/RequestContext"
import { RequestContextProvider } from "./requestContext/RequestContextProvider"

const messages = defineMessages({
	title: {
		defaultMessage: "Organizations",
		id: "dealerList.title",
	},
})

const env = currentEnvironment()

export const DealershipListContainer = () => {
	const [isInitialized, setIsInitialized] = useState(false)
	const [hasCognitoError, setHasCognitoError] = useState(false)

	const cognitoConfig = useMemo(() => getCognitoConfig(), [])

	const onCognitoError = useCallback((_type: RequestType, _error: unknown) => {
		setHasCognitoError(true)
	}, [])

	const { getAuthorizationHeader } = useCognito(cognitoConfig, onCognitoError)

	const getDealershipRedirect = useDealershipRedirect(getAuthorizationHeader)

	useEffect(() => {
		getDealershipRedirect().then((redirect) => {
			if (redirect) {
				window.location.assign(redirect.url)
				return
			}
			setIsInitialized(true)
		})
	}, [getDealershipRedirect])

	return (
		<TranslationsProvider locale={window.navigator.language ?? "en"}>
			<TranslationsWrapper>
				<AdobeAnalytics />
				<InnerDealershipListContainer
					getAuthorizationHeader={getAuthorizationHeader}
					isInitialized={isInitialized}
					hasCognitoError={hasCognitoError}
				/>
			</TranslationsWrapper>
		</TranslationsProvider>
	)
}

const InnerDealershipListContainer = ({
	getAuthorizationHeader,
	hasCognitoError,
	isInitialized,
}: {
	getAuthorizationHeader: RequestContextProps["getAuthorizationHeader"]
	hasCognitoError: boolean
	isInitialized: boolean
}) => {
	const legacyEnvironment = mapLegacyEnvironment(env)
	const jwtClaims = useMemo(() => getJwtPayload(), [])
	const hasNewsAccess = useHasNewsAccess()
	const { formatMessage } = useIntl()

	const navItems = getDealershipListNavItems({
		hasNewsAccess,
		isMarketManager: !!jwtClaims && isMarketManager(jwtClaims),
		labels: {
			dealerships: formatMessage(messages.title),
			news: formatMessage(newsListMessages.navigation),
		},
	})

	const requestContext: RequestContextProps = {
		environment: legacyEnvironment,
		getAuthorizationHeader,
	}
	return (
		<>
			<NestedAppTitle />
			{isInitialized ? (
				<>
					{navItems.length > 0 && (
						<MainNavigation logoLinkUrl="/" logoLabel="Car Sales" navItems={navItems} />
					)}
					<ModalProvider>
						<RequestContextProvider context={requestContext}>
							<ErrorBannerProvider>
								<DealershipListRoutes />
							</ErrorBannerProvider>
						</RequestContextProvider>
					</ModalProvider>
				</>
			) : (
				<FullPage>{hasCognitoError ? <CognitoErrorBanner /> : <PSpinner />}</FullPage>
			)}
		</>
	)
}

const FullPage = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100vh;
`

const TranslationsWrapper = ({ children }: PropsWithChildren) => {
	const { isLoaded } = useContext(TranslationsContext)
	return isLoaded ? children : null
}

const NestedAppTitle = () => {
	const { formatMessage } = useIntl()
	return <AppTitle title={formatMessage(messages.title)} locationCode={undefined} />
}
